import React from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { DialogFooter } from '@/components/ui/dialog'
import { sdk } from '@/lib/sdk'
import { CallSetupForm, emptyCallDraft, validateCallDraft } from '@/components/features/call4papers/CallSetupForm'
import { saveCall } from '@/components/features/call4papers/callPersistence'
import { CoursesSingleStep, emptyCourseDraft } from '@/components/features/spaces/wizard/modules/CoursesSingleStep'
import { slugify } from '@/components/features/courses/courseUtils'
import { SurveySingleStep } from '@/components/features/surveys/SurveySingleStep'
import { emptySurveyDraft, type SurveyDraft } from '@/components/features/surveys/surveysSpaceConfig'
import { FormSingleStep } from '@/components/features/forms/FormSingleStep'
import { emptyFormDraft, type FormDraft } from '@/components/features/forms/formsSpaceConfig'
import { DownloadSingleStep } from '@/components/features/downloads/DownloadSingleStep'
import type { SpaceModule } from '@/types/spaces'
import { ShowFields } from '@/modules/podcast/engine/ShowFields'
import { createShowFromDraft, emptyShowDraft, type ShowDraft } from '@/modules/podcast/engine/showDraft'
import {
  createDownloadSingle,
  downloadSingleFailure,
  emptyDownloadSingleDraft,
  validateDownloadSingle,
  type DownloadSingleDraft,
  type DownloadSingleErrors,
  type DownloadSingleProgress
} from '@/components/features/downloads/downloadSingle'
import type { UploadProgress } from '@/components/features/downloads/downloadUpload'
import type { DownloadFileLimits } from '@/types/downloads'
import type { CoursesModuleConfig } from '@/types/space-wizard'

type CourseDraft = NonNullable<CoursesModuleConfig['course']>

type ApiError = { message?: string; response?: { data?: { message?: string; errors?: Array<{ param?: string; message?: string }> } } } | null

/**
 * The item of a one-item space that was just switched to that mode while empty: the same form
 * the creation wizard shows, then the space opens on the item. "Later" leaves it as it is.
 */
export const SingleItemFollowUp: React.FC<{
  module: SpaceModule
  spaceId: string
  callType?: 'simple' | 'advanced'
  onDone: () => void
  onLater: () => void
}> = ({ module, spaceId, callType = 'simple', onDone, onLater }) => {
  const { t, i18n } = useTranslation(['common', 'call4papers', 'courses', 'surveys', 'forms', 'downloads', 'media'])
  const [busy, setBusy] = React.useState(false)
  const [error, setError] = React.useState('')
  const [callDraft, setCallDraft] = React.useState(emptyCallDraft)
  const [courseDraft, setCourseDraft] = React.useState<CourseDraft>(emptyCourseDraft)
  const [courseErrors, setCourseErrors] = React.useState<{ title?: string; level?: string; templateId?: string; text?: string }>({})
  const [surveyDraft, setSurveyDraft] = React.useState<SurveyDraft>(emptySurveyDraft)
  const [surveyTitleError, setSurveyTitleError] = React.useState<string | undefined>()
  const [formDraft, setFormDraft] = React.useState<FormDraft>(emptyFormDraft)
  const [formTitleError, setFormTitleError] = React.useState<string | undefined>()
  const [downloadDraft, setDownloadDraft] = React.useState<DownloadSingleDraft>(emptyDownloadSingleDraft)
  const [downloadErrors, setDownloadErrors] = React.useState<DownloadSingleErrors | null>(null)
  const [downloadDone, setDownloadDone] = React.useState<DownloadSingleProgress>({})
  const [showDraft, setShowDraft] = React.useState<ShowDraft>(() => emptyShowDraft(i18n.language))
  const [showTitleError, setShowTitleError] = React.useState<string | undefined>()
  const [upload, setUpload] = React.useState<UploadProgress | null>(null)
  const [fileLimits, setFileLimits] = React.useState<DownloadFileLimits | undefined>()
  const abort = React.useRef<AbortController | null>(null)
  const errorRef = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => {
    if (module !== 'downloads') return
    let alive = true
    sdk.downloads.getSpace(spaceId).then((c) => { if (alive) setFileLimits(c.fileLimits) }).catch(() => {})
    return () => { alive = false }
  }, [module, spaceId])
  /* Closing mid-upload stops the transfer; the draft item stays, as on the item's page. */
  React.useEffect(() => () => abort.current?.abort(), [])
  React.useEffect(() => { if (error) errorRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' }) }, [error])

  const fail = (caught: unknown) => {
    const err = caught as ApiError
    setError(t('common:spaces.followUp.failed', { detail: err?.response?.data?.message || err?.message || t('common:errors.generic') }))
  }

  const createCourse = async () => {
    const description = courseDraft.description.replace(/<[^>]*>/g, '').trim() ? courseDraft.description : undefined
    const body = {
      spaceId,
      title: courseDraft.title.trim(),
      level: courseDraft.level,
      ...(description ? { description } : {}),
      ...(courseDraft.certificate?.enabled
        ? { certificate: { enabled: true, templateId: courseDraft.certificate.templateId, text: courseDraft.certificate.text?.trim() ? courseDraft.certificate.text : null } }
        : {})
    }
    const base = slugify(courseDraft.title) || 'course'
    try {
      await sdk.courses.create({ ...body, slug: base })
    } catch (caught) {
      /* The slug is the community's: a taken one gets a short suffix, once. */
      if (!(caught as ApiError)?.response?.data?.errors?.some((e) => e.param === 'slug')) throw caught
      await sdk.courses.create({ ...body, slug: `${base}-${Date.now().toString(36).slice(-4)}` })
    }
  }

  const submit = async () => {
    setError('')
    if (module === 'call4papers') {
      const problem = validateCallDraft(callDraft, callType)
      if (problem) { setError(t(`call4papers:${problem}`)); return }
    }
    if (module === 'courses' && !courseDraft.title.trim()) { setCourseErrors({ title: t('courses:form.errors.titleRequired') }); return }
    if (module === 'surveys' && !surveyDraft.title.trim()) { setSurveyTitleError(t('surveys:editor.titleRequired')); return }
    if (module === 'forms' && !formDraft.title.trim()) { setFormTitleError(t('forms:editor.titleRequired')); return }
    if ((module === 'podcast' || module === 'videos') && !showDraft.title.trim()) { setShowTitleError(t('media:show.errors.titleRequired')); return }
    if (module === 'downloads') {
      const invalid = validateDownloadSingle(downloadDraft, t, i18n.language, fileLimits)
      setDownloadErrors(invalid)
      if (invalid) return
    }
    setBusy(true)
    try {
      if (module === 'call4papers') await saveCall({ spaceId, draft: callDraft, callType })
      if (module === 'courses') await createCourse()
      if (module === 'surveys') {
        await sdk.surveys.create({
          spaceId,
          title: surveyDraft.title.trim(),
          description: surveyDraft.description.replace(/<[^>]*>/g, '').trim() ? surveyDraft.description : null,
          template: surveyDraft.template,
          anonymity: surveyDraft.anonymity,
          responseMode: surveyDraft.responseMode
        })
      }
      if (module === 'forms') {
        await sdk.forms.create({
          spaceId,
          title: formDraft.title.trim(),
          description: formDraft.description.replace(/<[^>]*>/g, '').trim() ? formDraft.description : null
        })
      }
      if (module === 'podcast' || module === 'videos') await createShowFromDraft(spaceId, showDraft, module)
      if (module === 'downloads') {
        abort.current = new AbortController()
        setUpload({ percent: 0 })
        let done = downloadDone
        await createDownloadSingle(spaceId, downloadDraft, done, (next) => { done = next; setDownloadDone(next) }, setUpload, abort.current.signal)
      }
      onDone()
    } catch (caught) {
      if (module === 'downloads') {
        setUpload(null)
        const reason = downloadSingleFailure(caught, t)
        if (reason) setError(t('common:spaces.followUp.failed', { detail: reason }))
      } else {
        const fields = (caught as ApiError)?.response?.data?.errors ?? []
        if (module === 'courses') setCourseErrors({ title: fields.find((e) => e.param === 'title')?.message, level: fields.find((e) => e.param === 'level')?.message })
        if (module === 'surveys') setSurveyTitleError(fields.find((e) => e.param === 'title')?.message)
        if (module === 'forms') setFormTitleError(fields.find((e) => e.param === 'title')?.message)
        if (module === 'podcast' || module === 'videos') setShowTitleError(fields.find((e) => e.param === 'title')?.message)
        fail(caught)
      }
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col space-y-4" data-testid="single-item-follow-up" data-module={module}>
      <div className="min-h-0 flex-1 space-y-4 overflow-y-auto pr-1">
        <div role="status" className="rounded-lg bg-primary-50 p-3 text-sm text-gray-700">{t('common:spaces.followUp.hint')}</div>
        {error && <div ref={errorRef} role="alert" className="rounded-lg bg-red-50 p-3 text-sm text-red-600">{error}</div>}
        {module === 'call4papers' && (
          <div className="space-y-4">
            <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-900">{t('common:spaces.stepCall')}</h3>
            <CallSetupForm callType={callType} data={callDraft} onChange={(next) => { setCallDraft(next); setError('') }} disabled={busy} />
          </div>
        )}
        {module === 'courses' && (
          <CoursesSingleStep draft={courseDraft} onChange={(next) => { setCourseDraft(next); setCourseErrors({}) }}
            titleError={courseErrors.title} levelError={courseErrors.level}
            certificateErrors={{ templateId: courseErrors.templateId, text: courseErrors.text }} disabled={busy} />
        )}
        {module === 'surveys' && (
          <SurveySingleStep draft={surveyDraft} onChange={(next) => { setSurveyDraft(next); setSurveyTitleError(undefined) }}
            titleError={surveyTitleError} disabled={busy} />
        )}
        {module === 'forms' && (
          <FormSingleStep draft={formDraft} onChange={(next) => { setFormDraft(next); setFormTitleError(undefined) }}
            titleError={formTitleError} disabled={busy} />
        )}
        {(module === 'podcast' || module === 'videos') && (
          <ShowFields module={module} draft={showDraft} onChange={(next) => { setShowDraft(next); setShowTitleError(undefined) }}
            spaceId={spaceId} errors={showTitleError ? { title: showTitleError } : {}} disabled={busy} idPrefix="follow-up-show" />
        )}
        {module === 'downloads' && (
          <DownloadSingleStep draft={downloadDraft} onChange={(next) => { setDownloadDraft(next); setDownloadErrors(null) }}
            errors={downloadErrors} upload={busy ? upload : null} uploaded={Boolean(downloadDone.uploaded)} limits={fileLimits} disabled={busy} />
        )}
      </div>
      <DialogFooter className="flex-shrink-0 gap-2 border-t pt-4">
        <Button type="button" variant="outline" onClick={() => { abort.current?.abort(); onLater() }} disabled={busy && module !== 'downloads'} data-testid="follow-up-later">
          {t('common:spaces.followUp.later')}
        </Button>
        <Button type="button" variant="secondary" onClick={() => void submit()} disabled={busy} data-testid="follow-up-create">
          {busy ? t('common:spaces.followUp.creating') : t(`common:spaces.followUp.create.${module}`)}
        </Button>
      </DialogFooter>
    </div>
  )
}
